/**
 * Date/time utility functions for timezone-aware conversions
 * Backend stores everything in UTC, frontend displays/edits in organization timezone
 */

/**
 * Convert UTC ISO string to local datetime-local format (YYYY-MM-DDTHH:mm)
 * for use in datetime-local input fields
 * The result represents the time in the specified timezone
 */
export function utcToLocalDatetimeLocal(utcIsoString: string | null, timezone: string = 'UTC'): string {
  if (!utcIsoString) return '';
  
  try {
    // Parse UTC date
    const utcDate = new Date(utcIsoString);
    
    // Format in the target timezone for datetime-local input
    // datetime-local expects format: YYYY-MM-DDTHH:mm (no timezone, treated as local)
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    
    // Use Intl.DateTimeFormat to get parts in the target timezone
    const parts = formatter.formatToParts(utcDate);
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    const hour = parts.find(p => p.type === 'hour')?.value;
    const minute = parts.find(p => p.type === 'minute')?.value;
    
    return `${year}-${month}-${day}T${hour}:${minute}`;
  } catch (error) {
    console.error('Error converting UTC to local datetime-local:', error);
    // Fallback to simple slice if Intl API fails
    return utcIsoString.slice(0, 16);
  }
}

/**
 * Convert local datetime-local format (YYYY-MM-DDTHH:mm) to UTC ISO string
 * The input is from a datetime-local input, which is in the browser's local timezone.
 * We need to interpret it as being in the specified organization timezone and convert to UTC.
 * 
 * Note: datetime-local inputs work in the browser's local timezone. So if the org timezone
 * differs from the browser timezone, we need to adjust for the difference.
 */
export function localDatetimeLocalToUtc(localDatetime: string, timezone: string = 'UTC'): string {
  if (!localDatetime) return '';
  
  try {
    // Parse the datetime-local value (YYYY-MM-DDTHH:mm)
    // This value is in the browser's local timezone
    const [datePart, timePart] = localDatetime.split('T');
    if (!datePart || !timePart) return '';
    
    // Extract components
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    
    // For UTC timezone, simple conversion
    if (timezone === 'UTC') {
      return `${datePart}T${timePart}:00Z`;
    }
    
    // We need to find the UTC time that, when formatted in the target timezone, gives us our target time
    // Use binary search to find the correct UTC time
    
    // Start with a guess: UTC date/time corresponding to the local date/time
    const initialUtc = Date.UTC(year, month - 1, day, hour, minute);
    
    // Search range: ±24 hours from the initial guess
    let minUtc = initialUtc - 24 * 60 * 60 * 1000;
    let maxUtc = initialUtc + 24 * 60 * 60 * 1000;
    
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    
    const targetTime = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    
    // Binary search for the correct UTC time
    for (let i = 0; i < 50; i++) {
      const midUtc = Math.floor((minUtc + maxUtc) / 2);
      const midDate = new Date(midUtc);
      const formatted = formatter.formatToParts(midDate);
      const formattedTime = `${formatted.find(p => p.type === 'year')?.value}-${formatted.find(p => p.type === 'month')?.value}-${formatted.find(p => p.type === 'day')?.value}T${formatted.find(p => p.type === 'hour')?.value}:${formatted.find(p => p.type === 'minute')?.value}`;
      
      if (formattedTime === targetTime) {
        // The search matches only to the minute, so midUtc lands on an arbitrary
        // millisecond within that minute. Floor to the minute so stored times are
        // clean (no stray seconds/ms leaking into start/end_time).
        return new Date(Math.floor(midUtc / 60000) * 60000).toISOString();
      } else if (formattedTime < targetTime) {
        minUtc = midUtc + 1;
      } else {
        maxUtc = midUtc - 1;
      }

      if (maxUtc < minUtc) break;
    }

    // If binary search didn't converge, use the midpoint (floored to the minute).
    const resultUtc = Math.floor(Math.floor((minUtc + maxUtc) / 2) / 60000) * 60000;
    return new Date(resultUtc).toISOString();
  } catch (error) {
    console.error('Error converting local datetime-local to UTC:', error, 'timezone:', timezone);
    // Fallback: treat as UTC
    return `${localDatetime}:00Z`;
  }
}

/**
 * Format a UTC date string for display in the organization timezone
 */
export function formatDateInTimezone(utcIsoString: string | null, timezone: string = 'UTC', options?: Intl.DateTimeFormatOptions): string {
  if (!utcIsoString) return '';
  
  try {
    const date = new Date(utcIsoString);
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      ...options,
    }).format(date);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return new Date(utcIsoString).toLocaleString();
  }
}
